import classnames from 'classnames/bind'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

// Components
import Image from '@/components/common/Image'
import Insight from '@/components/shared/Insight'
import ProductCard from '@/components/shared/ProductCard'

// Libs
import useMedia from '@/hooks/useMedia'
import { useObserver } from '@/hooks/useObserver'
import { handleGtmClickMerchant, handleGtmViewItemList } from '@/methods/handleGtmEvent'
import handleRoutePushWithQuery from '@/methods/url/handleRoutePushWithQuery'

// Constants
import { GtmItemListName } from '@/constants/gtmEvent'
import { ImageSizes } from '@/constants/imageSizes'
import { ROUTE_PATH } from '@/constants/routePath'

// Styles
import { isLiveMallClub } from '@/constants/systemType'
import styles from './style.module.scss'

const cx = classnames.bind(styles)

interface IProps {
  isMerchantLive: boolean
  avatar: string
  name: string
  productTotalNumber: number
  roomId?: string
  memberInfoId: string
  merchantProductList: any
  gtmLocation?: string
  liveMerchantInfo?: any
}

const MerchantInfo = (props: IProps) => {
  const { isMerchantLive, roomId, memberInfoId, avatar, productTotalNumber, name, merchantProductList, gtmLocation, liveMerchantInfo } = props
  const router = useRouter()
  const { t } = useTranslation()
  const { isDesktop } = useMedia()
  const intersecCallback = () => {
    merchantProductList?.length > 0 &&
      handleGtmViewItemList({
        itemListName: GtmItemListName.RECOMMEND,
        productInfoList: merchantProductList,
      })
  }
  const { observerRef } = useObserver({ observerCallback: intersecCallback })

  const goToMerchantInfoPage = () => {
    if (isLiveMallClub) {
      handleRoutePushWithQuery({ router, pathname: `${ROUTE_PATH.PROFILE}/${memberInfoId}?type=1` })
      return
    }

    handleRoutePushWithQuery({ router, pathname: `${ROUTE_PATH.USER}/${memberInfoId}` })
    gtmLocation &&
      handleGtmClickMerchant({
        location: gtmLocation,
        merchantDetail: {
          merchantId: memberInfoId,
          merchantName: name,
        },
      })
  }

  return (
    <div className={cx('merchant-info')}>
      <div onClick={goToMerchantInfoPage} className={cx('merchant-info__header')}>
        <div className={cx('merchant-info__left')}>
          <div className={cx('merchant-info__image-outside')}>
            <div className={cx('merchant-info__image-inside')}>
              <Image
                alt='merchant-image'
                src={avatar ? avatar : '/images/default-head.png'}
                width={isDesktop ? 50 : 40}
                height={isDesktop ? 50 : 40}
                style={{ objectFit: 'cover' }}
                sizes={ImageSizes.headShot}
              ></Image>
            </div>

            {isMerchantLive && roomId && liveMerchantInfo && (
              <Insight
                className={cx('merchant-info__insight')}
                status='empty'
                theme='gray'
                position='absolute'
                bottom='0'
                left={isDesktop ? '37px' : '28px'}
                roomId={roomId}
                liveSourceType={liveMerchantInfo.liveSourceType}
                liveType={liveMerchantInfo.liveType}
              />
            )}
          </div>
          <div className={cx('merchant-info__detail')}>
            <div className={cx('merchant-info__name')}>{name}</div>
            <div className={cx('merchant-info__productTotalNumer')}>
              <div className={cx('merchant-info__productTotalNumer-number')}>{productTotalNumber}</div>
              <div className={cx('merchant-info__productTotalNumer-text')}>{t('Common_Total_Product_Without_Format')}</div>
            </div>
          </div>
        </div>
        <div className={cx('merchant-info__right')}>
          <div className={cx('merchant-info__more-item')}>{t('Product_More')}</div>
        </div>
      </div>

      {merchantProductList?.length > 0 && (
        <div className={cx('merchant-info__recommendation-product-list')}>
          {merchantProductList?.map((data: any) => (
            <ProductCard
              key={data?.productId}
              className={cx('merchant-info__recommendation-product-item')}
              productName={data?.productName}
              isPromo
              {...data}
              isMiniWidth={true}
              observer={observerRef.current}
              gtmItemEventParams={{ itemListName: GtmItemListName.RECOMMEND }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default MerchantInfo
